"use client";

import React from "react";
import { Typography } from "@mui/material";
import moment from "moment-timezone";
import { useTimezone } from "@/app/utils/TimezoneContext";

export const TimezonedTime = ({
  time,
  format = "h:mm A",
  variant = "body2",
  color = "textPrimary",
  component = "span",
  fallback = "Invalid date",
  ...props
}) => {
  const { timezone } = useTimezone();

  // Handle different input types
  let formattedTime;

  try {
    if (time?.toDate && typeof time.toDate === "function") {
      formattedTime = moment(time.toDate()).tz(timezone).format(format);
    } else if (time instanceof Date) {
      formattedTime = moment(time).tz(timezone).format(format);
    } else if (moment.isMoment(time)) {
      formattedTime = time.tz(timezone).format(format);
    } else if (typeof time === "string") {
      formattedTime = moment(time).tz(timezone).format(format);
    } else if (typeof time === "number") {
      formattedTime = moment.unix(time).tz(timezone).format(format);
    } else if (time?._seconds !== undefined) {
      // Handle Firestore timestamp objects
      formattedTime = moment.unix(time._seconds).tz(timezone).format(format);
    } else {
      formattedTime = fallback;
    }
  } catch (error) {
    console.error("Error formatting time:", error);
    formattedTime = fallback;
  }

  return (
    <Typography
      variant={variant}
      color={color}
      component={component}
      {...props}
      sx={[{
        fontSize: 13
      }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
      {formattedTime}
    </Typography>
  );
};

// New component for time ranges
export const TimezonedTimeRange = ({
  startTime,
  endTime,
  format = "h:mm A",
  variant = "body2",
  color = "textPrimary",
  component = "span",
  separator = " - ",
  fallback = "Invalid time range",
  ...props
}) => {
  const { timezone } = useTimezone();

  try {
    const formatTime = (time) => {
      if (time?.toDate && typeof time.toDate === "function") {
        return moment(time.toDate()).tz(timezone);
      } else if (time instanceof Date) {
        return moment(time).tz(timezone);
      } else if (moment.isMoment(time)) {
        return time.tz(timezone);
      } else if (typeof time === "string") {
        return moment(time).tz(timezone);
      } else if (typeof time === "number") {
        return moment.unix(time).tz(timezone);
      } else if (time?._seconds !== undefined) {
        return moment.unix(time._seconds).tz(timezone);
      }
      return null;
    };

    const start = formatTime(startTime);
    const end = formatTime(endTime);

    if (!start || !end || !start.isValid() || !end.isValid()) {
      return (
        <Typography
          variant={variant}
          color={color}
          component={component}
          {...props}
        >
          {fallback}
        </Typography>
      );
    }

    const formattedRange = `${start.format(format)}${separator}${end.format(
      format
    )}`;

    return (
      <Typography
        variant={variant}
        color={color}
        component={component}
        {...props}
      >
        {formattedRange}
      </Typography>
    );
  } catch (error) {
    console.error("Error formatting time range:", error);
    return (
      <Typography
        variant={variant}
        color={color}
        component={component}
        {...props}
      >
        {fallback}
      </Typography>
    );
  }
};

// New component for duration formatting
export const TimezonedDuration = ({
  startTime,
  endTime,
  variant = "body2",
  color = "textSecondary",
  component = "span",
  showSeconds = false,
  fallback = "Duration error",
  ...props
}) => {
  const { timezone } = useTimezone();

  try {
    const formatTime = (time) => {
      if (time?.toDate && typeof time.toDate === "function") {
        return moment(time.toDate()).tz(timezone);
      } else if (time instanceof Date) {
        return moment(time).tz(timezone);
      } else if (moment.isMoment(time)) {
        return time.tz(timezone);
      } else if (typeof time === "string") {
        return moment(time).tz(timezone);
      } else if (typeof time === "number") {
        return moment.unix(time).tz(timezone);
      } else if (time?._seconds !== undefined) {
        return moment.unix(time._seconds).tz(timezone);
      }
      return null;
    };

    const start = formatTime(startTime);
    const end = formatTime(endTime);

    if (!start || !end || !start.isValid() || !end.isValid()) {
      return (
        <Typography
          variant={variant}
          color={color}
          component={component}
          {...props}
        >
          {fallback}
        </Typography>
      );
    }

    const duration = end.diff(start, showSeconds ? "seconds" : "minutes");

    if (showSeconds) {
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = duration % 60;

      let durationText = "";
      if (hours > 0) {
        durationText += `${hours} hour${hours > 1 ? "s" : ""}`;
      }
      if (minutes > 0) {
        if (hours > 0) durationText += " ";
        durationText += `${minutes} min${minutes > 1 ? "s" : ""}`;
      }
      if (seconds > 0 && hours === 0) {
        if (minutes > 0) durationText += " ";
        durationText += `${seconds} sec${seconds > 1 ? "s" : ""}`;
      }

      return (
        <Typography
          variant={variant}
          color={color}
          component={component}
          {...props}
        >
          {durationText || "0 seconds"}
        </Typography>
      );
    } else {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;

      let durationText = "";
      if (hours > 0) {
        durationText += `${hours} hour${hours > 1 ? "s" : ""}`;
      }
      if (minutes > 0) {
        if (hours > 0) durationText += " ";
        durationText += `${minutes} min${minutes > 1 ? "s" : ""}`;
      }

      return (
        <Typography
          variant={variant}
          color={color}
          component={component}
          {...props}
        >
          {durationText || "0 minutes"}
        </Typography>
      );
    }
  } catch (error) {
    console.error("Error calculating duration:", error);
    return (
      <Typography
        variant={variant}
        color={color}
        component={component}
        {...props}
      >
        {fallback}
      </Typography>
    );
  }
};

// Helper function for other components to use
export const parseTimezoneAware = (dateTime, timezone) => {
  if (!dateTime) return null;

  try {
    if (dateTime?.toDate && typeof dateTime.toDate === "function") {
      return moment(dateTime.toDate()).tz(timezone);
    } else if (dateTime instanceof Date) {
      return moment(dateTime).tz(timezone);
    } else if (moment.isMoment(dateTime)) {
      return dateTime.tz(timezone);
    } else if (typeof dateTime === "string") {
      return moment(dateTime).tz(timezone);
    } else if (typeof dateTime === "number") {
      return moment.unix(dateTime).tz(timezone);
    } else if (dateTime?._seconds !== undefined) {
      return moment.unix(dateTime._seconds).tz(timezone);
    }
  } catch (error) {
    console.warn("Date parsing error:", error, dateTime);
  }
  return null;
};
